const PENDING_INVITE_KEY = 'pendingInviteToken';
const PENDING_INVITE_DATA_KEY = 'pendingInviteData';

export interface PendingInviteData {
  token: string;
  inviterDisplayName: string;
  inviterPictureUrl: string;
}

export function setPendingInviteToken(token: string): void {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    // Fail silently if localStorage is unavailable
  }
}

export function getPendingInviteToken(): string | null {
  try {
    return localStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingInviteToken(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // Fail silently if localStorage is unavailable
  }
}

export function setPendingInviteData(data: PendingInviteData): void {
  try {
    localStorage.setItem(PENDING_INVITE_DATA_KEY, JSON.stringify(data));
  } catch {
    // Fail silently if localStorage is unavailable
  }
}

export function getPendingInviteData(): PendingInviteData | null {
  try {
    const data = localStorage.getItem(PENDING_INVITE_DATA_KEY);
    if (!data) return null;
    return JSON.parse(data) as PendingInviteData;
  } catch {
    return null;
  }
}

export function clearPendingInviteData(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_DATA_KEY);
  } catch {
    // Fail silently if localStorage is unavailable
  }
}

const MOBILE_WEB_SESSION_KEY = 'mobileWebSession';

/**
 * Grant mobile web access for the current session.
 * Called after completing an invite flow so user can continue using the app.
 * Uses sessionStorage so access is revoked when the tab closes.
 */
export function grantMobileWebAccess(): void {
  try {
    sessionStorage.setItem(MOBILE_WEB_SESSION_KEY, 'true');
  } catch {
    // Fail silently if sessionStorage is unavailable
  }
}

/**
 * Check if mobile web access was granted for this session.
 */
export function hasMobileWebAccess(): boolean {
  try {
    return sessionStorage.getItem(MOBILE_WEB_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Check if user is in an invite flow (has pending token or data)
 * or has been granted mobile web access for this session.
 * Used to allow mobile web users through when they have a pending invite
 * or have completed the invite flow.
 */
export function isInInviteFlow(): boolean {
  return (
    getPendingInviteToken() !== null ||
    getPendingInviteData() !== null ||
    hasMobileWebAccess()
  );
}
